import { useState } from 'react';
import { BackHeader } from '../components/BackHeader';
import { 
  MapPin, Navigation, Clock, DollarSign, TrendingUp, 
  ChevronRight, Fuel, Calendar, CheckCircle, AlertCircle,
  Route as RouteIcon, Car
} from 'lucide-react';

interface JobStop {
  id: string;
  address: string;
  clientName: string;
  service: string;
  timeWindow: string;
  estimatedDuration: number;
  completed: boolean;
  coordinates: { lat: number; lng: number };
}

interface RoutePlan {
  stops: JobStop[];
  totalDistance: number;
  totalTime: number;
  fuelCost: number;
  optimized: boolean;
}

export default function RouteOptimizer() {
  const [route, setRoute] = useState<RoutePlan>({
    stops: [
      {
        id: '1',
        address: 'Torre A, Apto 305, Av. Principal',
        clientName: 'María García',
        service: 'Reparación de fuga',
        timeWindow: '09:00 - 10:00',
        estimatedDuration: 60,
        completed: true,
        coordinates: { lat: 10.5, lng: -66.8 },
      },
      {
        id: '2',
        address: 'Torre B, Apto 1201, Av. Principal',
        clientName: 'Carlos Mendoza',
        service: 'Instalación de lavamanos',
        timeWindow: '11:00 - 12:00',
        estimatedDuration: 90,
        completed: false,
        coordinates: { lat: 10.51, lng: -66.81 },
      },
      {
        id: '3',
        address: 'Calle 5, Casa 23, Urb. Las Palmas',
        clientName: 'Ana López',
        service: 'Mantenimiento AC',
        timeWindow: '14:00 - 15:00',
        estimatedDuration: 120,
        completed: false,
        coordinates: { lat: 10.52, lng: -66.79 },
      },
      {
        id: '4',
        address: 'Edif. Orion, Piso 8, Apto 8B',
        clientName: 'Pedro Ruiz',
        service: 'Revisión eléctrica',
        timeWindow: '16:00 - 17:00',
        estimatedDuration: 45,
        completed: false,
        coordinates: { lat: 10.49, lng: -66.82 },
      },
    ],
    totalDistance: 18.5,
    totalTime: 240,
    fuelCost: 850,
    optimized: true,
  });

  const [alternativeRoute, setAlternativeRoute] = useState<RoutePlan | null>(null);
  const [showOptimization, setShowOptimization] = useState(false);

  const completedStops = route.stops.filter(s => s.completed).length;
  const remainingStops = route.stops.filter(s => !s.completed).length;
  const currentStop = route.stops.find(s => !s.completed);

  const handleOptimize = () => {
    // Simulate route optimization
    setAlternativeRoute({
      ...route,
      totalDistance: 15.2,
      totalTime: 210,
      fuelCost: 700,
      optimized: true,
    });
    setShowOptimization(true);
  };

  const handleAcceptOptimization = () => {
    if (alternativeRoute) {
      setRoute(alternativeRoute);
      setAlternativeRoute(null);
      setShowOptimization(false);
    }
  };

  return (
    <div className="space-y-6">
      <BackHeader fallback='/home/dashboard' />
      {/* Header Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">Paradas hoy</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-3xl font-bold text-white">{completedStops}</p>
            <p className="text-slate-500">/ {route.stops.length}</p>
          </div>
          <p className="text-green-400 text-xs mt-1">{remainingStops} restantes</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">Distancia total</p>
          <p className="text-3xl font-bold text-white mt-1">{route.totalDistance}</p>
          <p className="text-slate-500 text-xs">kilómetros</p>
        </div>
        <div className="bg-[var(--v-surface-1)] p-4 rounded-xl border border-[var(--v-surface-3)]">
          <p className="text-slate-400 text-sm">Costo combustible</p>
          <p className="text-3xl font-bold text-yellow-400 mt-1">${route.fuelCost}</p>
          <p className="text-slate-500 text-xs">estimado</p>
        </div>
      </div>

      {/* Current Stop Card */}
      {currentStop && (
        <div className="bg-gradient-to-r from-blue-600/20 to-blue-700/20 p-5 rounded-xl border border-blue-500/30">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-400 text-sm font-medium flex items-center gap-2">
                <Navigation className="w-4 h-4" />
                SIGUIENTE PARADA
              </p>
              <h3 className="text-xl font-bold text-white mt-1">{currentStop.clientName}</h3>
              <p className="text-slate-300 mt-1">{currentStop.service}</p>
              <div className="flex items-center gap-4 mt-3 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {currentStop.address}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {currentStop.timeWindow}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{currentStop.estimatedDuration}</p>
              <p className="text-slate-400 text-sm">min</p>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2">
              <Navigation className="w-5 h-5" />
              Iniciar Navegación
            </button>
            <button className="px-4 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-lg">
              Llamar
            </button>
          </div>
        </div>
      )}

      {/* Route List */}
      <div className="bg-[var(--v-surface-1)] rounded-xl border border-[var(--v-surface-3)] overflow-hidden">
        <div className="p-4 border-b border-[var(--v-surface-3)] flex items-center justify-between">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <RouteIcon className="w-5 h-5 text-blue-400" />
            Ruta del Día
          </h3>
          <button 
            onClick={handleOptimize}
            className="text-blue-400 text-sm hover:text-blue-300 flex items-center gap-1"
          >
            <TrendingUp className="w-4 h-4" />
            Optimizar
          </button>
        </div>
        <div className="divide-y divide-slate-700">
          {route.stops.map((stop, index) => (
            <div 
              key={stop.id}
              className={`p-4 flex items-start gap-4 ${stop.completed ? 'opacity-50' : ''}`}
            >
              {/* Stop Number */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                stop.completed 
                  ? 'bg-green-500 text-white' 
                  : index === route.stops.findIndex(s => !s.completed)
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-400'
              }`}>
                {stop.completed ? '✓' : index + 1}
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className={`font-semibold ${stop.completed ? 'text-slate-500 line-through' : 'text-white'}`}>
                      {stop.clientName}
                    </h4>
                    <p className="text-slate-400 text-sm">{stop.service}</p>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {stop.address}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {stop.timeWindow}
                      </span>
                    </div>
                  </div>
                  <span className="text-slate-400 text-sm">{stop.estimatedDuration} min</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Optimization Suggestion */}
      {showOptimization && alternativeRoute && (
        <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 p-5 rounded-xl border border-green-500/30">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-white">¡Ruta optimizada encontrada!</h4>
              <p className="text-slate-300 text-sm mt-1">
                Podemos ahorrarte tiempo y combustible reordenando las paradas.
              </p>
              
              <div className="grid grid-cols-3 gap-3 mt-4">
                <div className="bg-[var(--v-surface-1)]/50 p-3 rounded-lg text-center">
                  <p className="text-green-400 font-bold text-xl">
                    -{Math.round((1 - alternativeRoute.totalDistance / route.totalDistance) * 100)}%
                  </p>
                  <p className="text-slate-400 text-xs">Distancia</p>
                </div>
                <div className="bg-[var(--v-surface-1)]/50 p-3 rounded-lg text-center">
                  <p className="text-green-400 font-bold text-xl">
                    -{Math.round((1 - alternativeRoute.totalTime / route.totalTime) * 100)}%
                  </p>
                  <p className="text-slate-400 text-xs">Tiempo</p>
                </div>
                <div className="bg-[var(--v-surface-1)]/50 p-3 rounded-lg text-center">
                  <p className="text-green-400 font-bold text-xl">
                    ${route.fuelCost - alternativeRoute.fuelCost}
                  </p>
                  <p className="text-slate-400 text-xs">Ahorro</p>
                </div>
              </div>

              <div className="flex gap-3 mt-4">
                <button
                  onClick={handleAcceptOptimization}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-medium"
                >
                  Aplicar optimización
                </button>
                <button
                  onClick={() => setShowOptimization(false)}
                  className="px-4 py-2 text-slate-400 hover:text-white"
                >
                  Mantener actual
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Stop */}
      <button className="w-full bg-[var(--v-surface-1)] hover:bg-slate-700 border-2 border-dashed border-slate-600 text-slate-400 py-4 rounded-xl font-medium flex items-center justify-center gap-2 transition">
        <MapPin className="w-5 h-5" />
        Agregar parada adicional
      </button>

      {/* Weekly Stats */}
      <div className="bg-[var(--v-surface-1)] p-5 rounded-xl border border-[var(--v-surface-3)]">
        <h4 className="font-semibold text-white mb-4">Estadísticas Semanales</h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">127 km</p>
              <p className="text-slate-400 text-sm">Esta semana</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
              <Fuel className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">$5,240</p>
              <p className="text-slate-400 text-sm">En combustible</p>
            </div>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-[var(--v-surface-3)]">
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <TrendingUp className="w-4 h-4" />
            <span>15% más eficiente que la semana pasada</span>
          </div>
        </div>
      </div>
    </div>
  );
}
